import { ChangeDetectionStrategy, Component, ViewEncapsulation, inject, signal } from "@angular/core";
import { MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { TranslocoModule, TranslocoService } from "@jsverse/transloco";
import { finalize } from "rxjs/operators";
import { NotifyService } from "@ui/notify";
import { TenantDataMigrationServiceProxy } from "@api/core";
import { getLegalDocument, LegalDocumentId } from "@app/wapps/legal/legal.model";

const LICENSE_TERMS_DOCUMENT_ID: LegalDocumentId = "service";

export interface MigrationConsentDialogResult {
    decided: boolean;
}

@Component({
    selector: "migration-consent-dialog",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    imports: [MatDialogModule, MatButtonModule, MatIconModule, TranslocoModule],
    templateUrl: "./migration-consent-dialog.component.html",
    styleUrls: ["./migration-consent-dialog.component.scss"],
})
export class MigrationConsentDialogComponent {
    private readonly _api = inject(TenantDataMigrationServiceProxy);

    private readonly _notify = inject(NotifyService);

    private readonly _transloco = inject(TranslocoService);

    private readonly _dialogRef: MatDialogRef<MigrationConsentDialogComponent, MigrationConsentDialogResult> =
        inject(MatDialogRef);

    readonly licenseTermsUrl = getLegalDocument(LICENSE_TERMS_DOCUMENT_ID).link;

    readonly submitting = signal(false);

    close(): void {
        this._dialogRef.close();
    }

    decide(accepted: boolean): void {
        this.submitting.set(true);

        this._api
            .consent(accepted)
            .pipe(finalize(() => this.submitting.set(false)))
            .subscribe({
                next: () => {
                    if (accepted) {
                        this._notify.success(this._transloco.translate("migration-consent.acceptedMessage"));
                    } else {
                        this._notify.info(this._transloco.translate("migration-consent.refusedMessage"));
                    }

                    this._dialogRef.close({ decided: true });
                },
                error: () => this._notify.error(this._transloco.translate("migration-consent.errorMessage")),
            });
    }
}
